import {Injectable} from '@angular/core';
import {Container, TextArea} from '../model/layout.model';
import {DocumentModel} from '../model/document.model';

/** Wohin der Kopiervorgang schreibt. */
export type KiTexteZiel = 'videotext' | 'cosmo';

/** Ein Feld des Kopiervorgangs: eine KI-Texte-Box und ihre Entsprechung je Ziel. */
interface KiTexteFeld {
  quelle: string;
  videotext: string;
  cosmo: string;
  label: string;
}

/** Was der Kopiervorgang bewirkt hat – Grundlage für die Rückmeldung an den Redakteur. */
export interface KiTexteUebernahmeErgebnis {
  /** Boxen, deren Text übernommen wurde. */
  felder: number;
  /** Boxen, deren KI-Texte-Quelle leer war und deshalb übersprungen wurde. */
  uebersprungen: string[];
  /** Boxen, deren Ziel-Box im Dokument nicht gefunden wurde. */
  fehlend: string[];
}

/**
 * Stumpfer Kopiervorgang für die Knöpfe "für Videotext übernehmen" / "für COSMO
 * übernehmen" im Reiter "KI-Texte" von Nordrhein-Westfalen.
 *
 * Anders als {@link CosmoTakeoverService} (Grafik-Werte) oder der ältere KiButton-Weg
 * (`chat/hrtext`) ruft dieser Vorgang **keine** KI auf – er kopiert lediglich den bereits
 * erzeugten Text aus einer `wdr.kitexte.*`-Box eins zu eins in die gleichbedeutende Box
 * unter Videotext bzw. COSMO. Eine leer gebliebene Quelle wird übersprungen, keine leere
 * Zielbox erzeugt.
 *
 * "Aussichten" hat unter Videotext und COSMO nur EIN kombiniertes Feld für alle drei
 * Aussichtentage, während die KI-Texte drei getrennte Boxen führen – die beiden Ziele
 * füllen es deshalb unterschiedlich: Videotext bekommt ausschließlich den 1. Aussichtentag
 * (dieselbe Zuordnung, die die bestehende Langwetter->Videotext-KiButton-Mapping im
 * Template für dasselbe Zielfeld nutzt), COSMO alle drei Tage hintereinander, durch einen
 * Absatz getrennt – COSMO hat kein separates Feld je Tag, muss die Aussicht über alle drei
 * Tage also in einem Text abbilden.
 */
@Injectable({providedIn: 'root'})
export class KiTexteTakeoverService {

  private static readonly EINFACHE_FELDER: KiTexteFeld[] = [
    {
      quelle: 'wdr.kitexte.heute',
      videotext: 'wdr.videotext.nordrheinwestfalenvideotext.heute',
      cosmo: 'wdr.cosmo.cosmovorhersagenrwbremenberlin.heute',
      label: 'Heute'
    },
    {
      quelle: 'wdr.kitexte.nacht',
      videotext: 'wdr.videotext.nordrheinwestfalenvideotext.nacht',
      cosmo: 'wdr.cosmo.cosmovorhersagenrwbremenberlin.nacht',
      label: 'Nacht'
    },
    {
      quelle: 'wdr.kitexte.morgen',
      videotext: 'wdr.videotext.nordrheinwestfalenvideotext.morgen',
      cosmo: 'wdr.cosmo.cosmovorhersagenrwbremenberlin.morgen',
      label: 'Morgen'
    },
  ];

  private static readonly AUSSICHTEN_LABEL = 'Aussichten (3 Tage)';
  private static readonly AUSSICHTEN_TAGE = [
    'wdr.kitexte.aussichten.tag1',
    'wdr.kitexte.aussichten.tag2',
    'wdr.kitexte.aussichten.tag3',
  ];
  private static readonly AUSSICHTEN_ZIEL: Record<KiTexteZiel, string> = {
    videotext: 'wdr.videotext.nordrheinwestfalenvideotext.aussichtendreitage',
    cosmo: 'wdr.cosmo.cosmovorhersagenrwbremenberlin.aussichten',
  };

  public uebernehmen(document: DocumentModel, ziel: KiTexteZiel): KiTexteUebernahmeErgebnis {
    const ergebnis: KiTexteUebernahmeErgebnis = {felder: 0, uebersprungen: [], fehlend: []};
    for (const feld of KiTexteTakeoverService.EINFACHE_FELDER) {
      const quelle = this.findeContainer(document.tabs, feld.quelle);
      const inhalt = quelle && this.istTextArea(quelle) ? (quelle as TextArea).content : undefined;
      if (!inhalt) {
        ergebnis.uebersprungen.push(feld.label);
        continue;
      }
      const zielContainerId = ziel === 'videotext' ? feld.videotext : feld.cosmo;
      this.schreibe(document, zielContainerId, inhalt, feld.label, ergebnis);
    }
    this.uebernehmeAussichten(document, ziel, ergebnis);
    return ergebnis;
  }

  /**
   * Videotext bekommt nur den 1. Aussichtentag (ein einzelnes Feld, wie beim
   * Langwetter-Weg). COSMO bekommt alle drei Tage hintereinander, durch einen Absatz
   * getrennt – leer gebliebene Tage werden dabei einfach ausgelassen, nicht als Lücke
   * mitgeschrieben.
   */
  private uebernehmeAussichten(document: DocumentModel, ziel: KiTexteZiel,
                                ergebnis: KiTexteUebernahmeErgebnis): void {
    const tage = ziel === 'videotext'
      ? [KiTexteTakeoverService.AUSSICHTEN_TAGE[0]]
      : KiTexteTakeoverService.AUSSICHTEN_TAGE;
    const texte = tage
      .map(id => this.findeContainer(document.tabs, id))
      .filter((c): c is Container => !!c && this.istTextArea(c))
      .map(c => (c as TextArea).content)
      .filter((inhalt): inhalt is string => !!inhalt);
    if (texte.length === 0) {
      ergebnis.uebersprungen.push(KiTexteTakeoverService.AUSSICHTEN_LABEL);
      return;
    }
    this.schreibe(document, KiTexteTakeoverService.AUSSICHTEN_ZIEL[ziel],
      texte.join('\n\n'), KiTexteTakeoverService.AUSSICHTEN_LABEL, ergebnis);
  }

  private schreibe(document: DocumentModel, zielContainerId: string, inhalt: string,
                    label: string, ergebnis: KiTexteUebernahmeErgebnis): void {
    const zielContainer = this.findeContainer(document.tabs, zielContainerId);
    if (!zielContainer || !this.istTextArea(zielContainer)) {
      ergebnis.fehlend.push(label);
      return;
    }
    (zielContainer as TextArea).content = inhalt;
    ergebnis.felder++;
  }

  private istTextArea(container: Container): boolean {
    return container.containerType === 'TextArea';
  }

  private findeContainer(container: Container[], containerId: string): Container | undefined {
    for (const kind of container) {
      if (kind.containerId === containerId) {
        return kind;
      }
      const tiefer = this.findeContainer(kind.containerList ?? [], containerId);
      if (tiefer) {
        return tiefer;
      }
    }
    return undefined;
  }
}
